import React, { useRef, useState } from "react";
import { Button, Form, ListGroup } from "react-bootstrap";
import { FaFileAlt, FaFilePdf, FaFileWord, FaTrash } from "react-icons/fa";
import { BiUpload } from "react-icons/bi";
import api from "common/api";
import { toast } from "react-toastify";
import { useAuth } from "../../../../authContext";
import { ICourse } from "common/types";
import DeleteFileModal from "./DeleteFileModal";

type Props = {
  course: ICourse;
  setCourse: (course: ICourse) => void;
};

const CourseFiles: React.FC<Props> = ({ course, setCourse }) => {
  const { userProfile } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);

  const getFileIcon = (filePath: string) => {
    if (filePath.endsWith(".pdf")) {
      return <FaFilePdf />;
    } else if (filePath.endsWith(".doc")) {
      return <FaFileWord />;
    } else {
      return <FaFileAlt />;
    }
  };

  const deleteFile = async () => {
    if (fileToDelete === null) return;

    try {
      await api.delete(`/materials/file/${fileToDelete}/`);
      toast.success("Datoteka je uspješno izbrisana.");
      // Update course files
      setCourse({
        ...course,
        files: course.files.filter((file) => file.id !== fileToDelete),
      });
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting file: ", error);
      toast.error("Greška prilikom brisanja datoteke.");
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setIsUploading(true);
      const formData = new FormData();
      formData.append("name", file);

      try {
        const response = await api.post(
          `/courses/${course.id}/file/`,
          formData,
        );
        toast.success("Datoteka je uspješno učitana.");
        setCourse({
          ...course,
          files: [...course.files, response.data],
        });
      } catch (error) {
        console.error("Error uploading file: ", error);
        toast.error("Greška prilikom učitavanja datoteke.");
      }

      setIsUploading(false);
    }
  };

  return (
    <div>
      <h4>Datoteke</h4>
      {course && course.files && course.files.length > 0 ? (
        <ListGroup style={{ cursor: "pointer" }}>
          {course.files
            .filter((file) => file.name !== null)
            .map((file) => (
              <ListGroup.Item
                key={file.id}
                className="d-flex justify-content-between align-items-center"
              >
                <span>
                  <a
                    href={file.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    {getFileIcon(file.name)} {file.name.split("/").pop()}
                  </a>
                </span>
                {(userProfile?.role === "PRINCIPAL" ||
                  userProfile?.role === "TEACHER") && (
                  <Button
                    variant="danger"
                    onClick={() => {
                      setFileToDelete(file.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    <FaTrash />
                  </Button>
                )}
              </ListGroup.Item>
            ))}
        </ListGroup>
      ) : (
        <p className="text-muted">Trenutno nema datoteka za ovu grupu</p>
      )}

      {userProfile?.role !== "STUDENT" && (
        <Button
          variant="primary"
          className="mt-3"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <BiUpload /> Upload datoteke
        </Button>
      )}
      <Form.Control
        type="file"
        onChange={handleFileChange}
        disabled={isUploading}
        className="mt-2"
        style={{ display: "none" }}
        ref={fileInputRef}
      />

      <DeleteFileModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={deleteFile}
      />
    </div>
  );
};

export default CourseFiles;
